import React, { useEffect, useMemo, useState, lazy, createElement } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeftIcon, LockIcon, CheckCircleIcon, BrainIcon, AwardIcon, LaptopIcon, DollarSignIcon, GraduationCapIcon } from 'lucide-react';
import { TrackingNoticeLinks } from '../components/TrackingNoticeLinks';
import { getUniversityBySlug, getProgramBySlug } from '../data/universityData';
import { useMetaTags } from '../hooks/useMetaTags';
import { getPublisherParams } from '../utils/publisherParams';
import { trackFormSubmit } from '../utils/trackFormSubmit';
import { getMetaCookies, resolveSub3 } from '../utils/metaCookies';
import { validateAddress, lookupZip } from '../utils/addressValidation';
// Form value → API value mappings
const educationToApiValue = (v: string): number => {
  const map: Record<string, number> = {
    'high-school': 1,
    'some-high-school': 2,
    ged: 3,
    'some-college': 5,
    associates: 5,
    bachelors: 7,
    masters: 7,
    doctorate: 7
  };
  return map[v] || 9;
};
const startTimeframeToApiValue = (v: string): number => {
  const map: Record<string, number> = {
    immediately: 1,
    '1-3-months': 2,
    '4-6-months': 3,
    '3-6-months': 3,
    '6-12-months': 4,
    '1-year-plus': 4,
    undecided: 6
  };
  return map[v] || 6;
};
const generateUUID = (): string => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : r & 0x3 | 0x8;
    return v.toString(16);
  });
};
const getDeviceType = (): string => {
  const ua = navigator.userAgent;
  if (/mobile/i.test(ua)) return 'mobile';
  if (/tablet|ipad/i.test(ua)) return 'tablet';
  return 'desktop';
};
export function AdvertiserTemplatePage() {
  const {
    collegeSlug,
    programSlug
  } = useParams<{
    collegeSlug: string;
    programSlug: string;
  }>();
  const navigate = useNavigate();
  const location = useLocation();
  const university = collegeSlug ? getUniversityBySlug(collegeSlug) : undefined;
  const initialProgram = university && programSlug ? getProgramBySlug(university, programSlug) : null;
  // Capture optional tracking params from the URL once at mount.
  // sub1/sub2/sub3 + the rest of the documented optional fields can all
  // be passed in via query string and will be forwarded on submit.
  const trackingParams = useMemo(() => {
    const q = new URLSearchParams(location.search);
    return {
      sub1: q.get('sub1') || '',
      sub2: q.get('sub2') || '',
      sub3: q.get('sub3') || '',
      userIP: q.get('userIP') || '',
      webInitiatingURL: q.get('webInitiatingURL') || document.referrer || '',
      webLandingURL: q.get('webLandingURL') || window.location.href,
      inventoryType: q.get('inventoryType') || '',
      mediaType: q.get('mediaType') || '1'
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Inject LeadID + TrustedForm scripts AFTER first paint so they don't
  // compete with the initial render. They still load early enough to
  // observe user form interaction and produce a valid certificate.
  useEffect(() => {
    let leadIdScript: HTMLScriptElement | null = null;
    let tfScript: HTMLScriptElement | null = null;
    const inject = () => {
      ;
      (window as any).myCallbackFunction = function (token: string) {
        const field = document.getElementsByName('leadid_token')[0] as HTMLInputElement | undefined;
        if (field) field.setAttribute('value', token);
      };
      leadIdScript = document.createElement('script');
      leadIdScript.id = 'LeadiDscript_campaign';
      leadIdScript.type = 'text/javascript';
      leadIdScript.async = true;
      leadIdScript.src = '//create.lidstatic.com/campaign/d50a3557-9854-7b8f-792c-527750401f5f.js?snippet_version=2&callback=myCallbackFunction';
      document.body.appendChild(leadIdScript);
      tfScript = document.createElement('script');
      tfScript.type = 'text/javascript';
      tfScript.async = true;
      tfScript.src = 'http' + ('https:' === document.location.protocol ? 's' : '') + '://api.trustedform.com/trustedform.js?provide_referrer=' + escape('false') + '&field=' + escape('xxTrustedFormCertUrl') + '&l=' + new Date().getTime() + Math.random();
      document.body.appendChild(tfScript);
    };
    const ric = (window as any).requestIdleCallback as ((cb: () => void, opts?: {
      timeout: number;
    }) => number) | undefined;
    const handle = ric ? ric(inject, {
      timeout: 2000
    }) : window.setTimeout(inject, 1200) as unknown as number;
    return () => {
      const cancelIdle = (window as any).cancelIdleCallback as ((h: number) => void) | undefined;
      if (ric && cancelIdle) cancelIdle(handle);else window.clearTimeout(handle);
      try {
        leadIdScript?.parentNode?.removeChild(leadIdScript);
      } catch {}
      try {
        tfScript?.parentNode?.removeChild(tfScript);
      } catch {}
    };
  }, []);
  const metaDescription = university ? initialProgram ? `Explore the ${initialProgram.degreeLevel ? `${initialProgram.degreeLevel} ` : ''}${initialProgram.name} program at ${university.name} and request information about your next steps.` : `Explore online degree programs at ${university.name} and request information about options that align with your educational goals.` : 'The requested university program could not be found. Explore accredited online degree programs on FindOnlineDegrees.';
  useMetaTags({
    title: university ? `${university.name} | Request Information` : 'Program Not Found',
    description: metaDescription,
    ogImage: university?.landingContent?.communityImage?.url,
    ogType: 'website',
    // Partner advertiser pages are paid lead-gen surfaces; keep them out
    // of the index so they don't compete with the brand site for SEO.
    robots: 'noindex, follow'
  });
  // Campus-aware advertisers (e.g. SJVC) use a step 0 to look up the user's
  // campus by zip before showing program options.
  const hasCampusFlow = Boolean(university?.campusZipLookup);
  const firstStep = hasCampusFlow ? 0 : 1;
  const totalSteps = hasCampusFlow ? 5 : 4;
  const [step, setStep] = useState(firstStep);
  const [formData, setFormData] = useState({
    degreeLevel: initialProgram?.degreeLevel || '',
    areaOfStudy: initialProgram?.category || '',
    program: initialProgram?.name || '',
    highestEducation: '',
    startDate: '',
    hsGradYear: '',
    zipCode: '',
    city: '',
    state: '',
    streetAddress: '',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    consent: false,
    consentSms: false,
    campusId: 0,
    campusName: ''
  });
  const [error, setError] = useState('');
  const [campusLookupError, setCampusLookupError] = useState('');
  // True while the ZIP is being verified on the address step.
  const [addressChecking, setAddressChecking] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  // Auto-advance Step 2
  useEffect(() => {
    if (step === 2 && formData.highestEducation && formData.startDate && formData.hsGradYear) {
      const timer = setTimeout(() => {
        setError('');
        setStep(3);
      }, 400);
      return () => clearTimeout(timer);
    }
  }, [formData.highestEducation, formData.startDate, formData.hsGradYear, step]);
  // ZIP code lookup — shares the cached lookup used by address validation, so
  // verifying on Continue doesn't re-request a ZIP already resolved here.
  useEffect(() => {
    if (formData.zipCode.length !== 5) return;
    let active = true;
    lookupZip(formData.zipCode).then(place => {
      if (!active || !place) return;
      setFormData(prev => ({
        ...prev,
        city: place.city,
        state: place.state
      }));
    });
    return () => {
      active = false;
    };
  }, [formData.zipCode]);
  if (!university) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4" data-id="element-813" data-mp="27akkxj5egujj">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 max-w-md w-full text-center" data-id="element-814" data-mp="22c9952vjo9yo">
          <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-815" data-mp="1xfiumjze1mfd">
            Program Not Found
          </h2>
          <p className="text-gray-600 mb-6" data-id="element-816" data-mp="1sly6rkrie8tw">
            We couldn't find the specific program or university you're looking
            for.
          </p>
          <button onClick={() => navigate('/')} className="w-full py-3 bg-gray-900 text-white rounded-lg font-bold hover:bg-gray-800 transition-colors" data-id="element-817" data-mp="1jncfn17z1lhi">
            Return Home
          </button>
        </div>
      </div>;
  }
  const updateField = (field: string, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setError('');
  };
  const handleContinue = async () => {
    if (step === 0) {
      if (!formData.zipCode || formData.zipCode.length !== 5) {
        setCampusLookupError('Please enter your 5-digit ZIP code to continue.');
        return;
      }
      if (!formData.campusId) {
        setCampusLookupError(`Sorry, that ZIP code isn't served by a ${university.shortName} campus. Please check your ZIP and try again.`);
        return;
      }
      const campusPrograms = university.programsForCampus ? university.programsForCampus(formData.campusId) : university.programs;
      const preselectStillValid = !!initialProgram && campusPrograms.some(p => p.name === initialProgram.name);
      setFormData(prev => ({
        ...prev,
        degreeLevel: preselectStillValid ? initialProgram!.degreeLevel || '' : '',
        areaOfStudy: preselectStillValid ? initialProgram!.category : '',
        program: preselectStillValid ? initialProgram!.name : ''
      }));
      setCampusLookupError('');
      setStep(1);
      return;
    }
    if (step === 1) {
      if (university.singleProgramSelect) {
        if (!formData.program) {
          setError('Please select a program to continue.');
          return;
        }
      } else if (!formData.degreeLevel || !formData.areaOfStudy || !formData.program) {
        setError('Please complete all fields to continue.');
        return;
      }
      setStep(2);
    } else if (step === 2) {
      if (!formData.highestEducation || !formData.startDate || !formData.hsGradYear) {
        setError('Please complete all fields to continue.');
        return;
      }
      setStep(3);
    } else if (step === 3) {
      // Verify the street line looks like an address and that the ZIP exists
      // before the lead is posted. A lookup outage never blocks the user.
      setAddressChecking(true);
      const check = await validateAddress({
        streetAddress: formData.streetAddress,
        zipCode: formData.zipCode,
        state: formData.state
      });
      setAddressChecking(false);
      if (!check.valid) {
        setError(check.message);
        return;
      }
      if (check.place) {
        setFormData(prev => ({
          ...prev,
          city: prev.city || check.place!.city,
          state: prev.state || check.place!.state
        }));
      }
      setStep(4);
    }
  };
  const formatPhoneNumber = (value: string) => {
    const digits = value.replace(/\D/g, '');
    if (digits.startsWith('1')) return formData.phone;
    const limited = digits.slice(0, 10);
    if (limited.length <= 3) return limited;
    if (limited.length <= 6) return `${limited.slice(0, 3)}-${limited.slice(3)}`;
    return `${limited.slice(0, 3)}-${limited.slice(3, 6)}-${limited.slice(6)}`;
  };
  const handleSubmit = () => {
    if (!formData.firstName.trim() || !formData.lastName.trim()) {
      setError('Please enter your full name.');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setError('Please enter a valid email address.');
      return;
    }
    if (formData.phone.replace(/\D/g, '').length !== 10) {
      setError('Please enter a valid 10-digit phone number.');
      return;
    }
    if (!university.consentOnSubmit && !formData.consent) {
      setError('Please agree to the consent terms.');
      return;
    }
    if (university.tcpaTextSms && !formData.consentSms) {
      setError('Please check both consent boxes to continue.');
      return;
    }
    setIsSubmitting(true);
    // Read tracking values populated by the LeadID + TrustedForm scripts
    const leadIdInput = document.getElementsByName('leadid_token')[0] as HTMLInputElement | undefined;
    const tfInput = document.getElementsByName('xxTrustedFormCertUrl')[0] as HTMLInputElement | undefined;
    const leadIdToken = leadIdInput?.value || generateUUID();
    const trustedFormUrl = tfInput?.value || '';
    // Build the full lead payload per advertiser parameter spec.
    // Includes: required advertiser identifiers, mapped form values,
    // PII, and optional tracking parameters.
    const payload: Record<string, string> = {
      // Required advertiser identifiers. URL params (mPubGroupID, or
      // mPubID + mPubCampaignID) override the per-university config values
      // when present; otherwise fall back to the university config.
      ...getPublisherParams(location.search, {
        mPubID: university.mPubID || '',
        mPubCampaignID: university.mPubCampaignID || ''
      }),
      mBrandID: university.mBrandID || '',
      hpRequestId: generateUUID(),
      mSessionID: `s-${generateUUID()}`,
      leadIdToken,
      // Mapped form values. When the partner provides a numeric program
      // catalog (programId), post that ID as mProgram; otherwise fall back
      // to the program name.
      degreeInterest: formData.degreeLevel,
      areaStudy: formData.areaOfStudy,
      mProgram: (() => {
        const matched = programsForThisFlow.find(p => p.name === formData.program);
        if (matched?.mProgramValue) return matched.mProgramValue;
        return matched?.programId ? String(matched.programId) : formData.program;
      })(),
      highestEducation: String(educationToApiValue(formData.highestEducation)),
      startTimeframe: String(startTimeframeToApiValue(formData.startDate)),
      gradYear: formData.hsGradYear,
      // PII
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      phone: formData.phone.replace(/\D/g, ''),
      address: formData.streetAddress,
      city: formData.city,
      state: formData.state,
      zipCode: formData.zipCode,
      campusLocation: formData.campusId ? String(formData.campusId) : '',
      smsConsent: formData.consentSms ? '1' : '0',
      // Optional tracking
      useragent: navigator.userAgent,
      sub1: trackingParams.sub1,
      sub2: trackingParams.sub2,
      // sub3 carries the Meta match keys ("<fbc>_<fbp>") for the Conversions
      // API when the Pixel cookies are present.
      sub3: resolveSub3(window.location.search) || trackingParams.sub3,
      userIP: trackingParams.userIP,
      webInitiatingURL: trackingParams.webInitiatingURL,
      webLandingURL: trackingParams.webLandingURL,
      trustedFormUrl,
      inventoryType: trackingParams.inventoryType,
      deviceType: getDeviceType(),
      mediaType: trackingParams.mediaType
    };
    // Meta match keys for server-side (CAPI) attribution.
    const {
      fbp,
      fbc
    } = getMetaCookies();
    if (fbp) payload.fbp = fbp;
    if (fbc) payload.fbc = fbc;
    console.log('[Advertiser Lead] payload:', payload);
    // Report the submission to GTM once (and only once) the lead has been
    // sent. `leadIdToken` doubles as the dedupe id for Meta CAPI.
    let reported = false;
    const reportSubmission = () => {
      if (reported) return;
      reported = true;
      trackFormSubmit({
        formId: 'university-request-info',
        pageUrl: window.location.pathname,
        leadId: leadIdToken
      });
    };
    // Host-and-post: the advertiser provides a posting endpoint per partner.
    // Configure it via university.leadPostUrl. If unset, no post is fired
    // (the payload is still logged above for QA / verification).
    const postBase = university.leadPostUrl || '';
    if (postBase) {
      const qs = new URLSearchParams(payload).toString();
      const fullUrl = `${postBase}?${qs}`;
      console.log('[Advertiser Lead] POST URL:', fullUrl);
      try {
        const pixel = new Image();
        pixel.onload = reportSubmission;
        pixel.src = fullUrl;
      } catch (err) {
        console.error('[Advertiser Lead] post error:', err);
      }
    } else {
      console.log('[Advertiser Lead] No leadPostUrl configured for this advertiser — skipping post.');
    }
    // The conversion is reported through GTM (dataLayer `form_submission`)
    // once the lead post is confirmed — no Meta Pixel event is fired here.
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSuccess(true);
      // Advertisers without a configured posting endpoint never produce a
      // pixel load, so confirm on the success state instead.
      reportSubmission();
      setTimeout(() => {
        navigate('/');
      }, 3000);
    }, 1000);
  };
  // Derived program list — honors campus-aware advertisers (filters to the
  // programs available at the user's matched campus).
  const programsForThisFlow = hasCampusFlow && formData.campusId && university.programsForCampus ? university.programsForCampus(formData.campusId) : university.programs;
  // Derived options for Step 1
  const uniqueAreas = Array.from(new Set(programsForThisFlow.filter(p => !formData.degreeLevel || p.degreeLevel === formData.degreeLevel).map(p => p.category)));
  const availablePrograms = programsForThisFlow.filter(p => (!formData.degreeLevel || p.degreeLevel === formData.degreeLevel) && (!formData.areaOfStudy || p.category === formData.areaOfStudy));
  const currentYear = new Date().getFullYear();
  // Years run back to 1990; everything earlier is collapsed into the
  // "1989 or earlier" option, which posts 1989.
  const years = Array.from({
    length: currentYear - 1989
  }, (_, i) => currentYear - i);
  const selectStyle = {
    backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
    backgroundPosition: 'right 0.5rem center',
    backgroundRepeat: 'no-repeat',
    backgroundSize: '1.5em 1.5em',
    paddingRight: '2.5rem'
  };
  if (isSuccess) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4" data-id="element-818" data-mp="kyt65ae58npk">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-10 max-w-md w-full text-center" data-id="element-819" data-mp="l0xb7vns3srf">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-820" data-mp="b8zuk9e048lb">
            <CheckCircleIcon className="w-8 h-8 text-green-600" data-id="element-821" data-mp="6a6huj6jglm7" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-822" data-mp="1ak3u7ivz1cc">
            Thanks, {formData.firstName}!
          </h2>
          <p className="text-gray-600" data-id="element-823" data-mp="3tdutel2codji">
            {university.name} will reach out shortly. Redirecting you to the
            homepage...
          </p>
        </div>
      </div>;
  }
  // Dynamic hero title — show the selected program once chosen, otherwise a generic prompt.
  // On the first step (entry view), prefer the partner's high-CTR landing headline if provided.
  const isEntryStep = step === firstStep;
  // Once the user has chosen both a program and a degree level, surface the
  // degree in the H1 (e.g. "Bachelor's in Early Childhood Education").
  const selectedProgramTitle = formData.program && formData.degreeLevel ? `${formData.degreeLevel} in ${formData.program}` : formData.program || initialProgram?.name;
  const heroTitle = isEntryStep && university.landingContent ? university.landingContent.headline : selectedProgramTitle || `Find Your ${university.shortName} Program`;
  // Show the fuller landing subheadline on every step (entry and beyond) so
  // the program-selection / later steps aren't left with just the short
  // mission line. Falls back to the mission when no landing copy exists.
  const heroDescription = university.landingContent?.subheadline !== undefined ? university.landingContent.subheadline : university.mission;
  const featureIconMap: Record<string, any> = {
    BrainIcon,
    AwardIcon,
    LaptopIcon,
    DollarSignIcon,
    GraduationCapIcon
  };
  return <div className="min-h-screen font-sans flex flex-col bg-gray-50" data-id="element-824" data-mp="3k6f6nketz79a">
      {/* Hidden tracking fields populated by LeadID + TrustedForm scripts */}
      <input type="hidden" name="leadid_token" value="" data-id="element-825" data-mp="3tz46fi10qwos" />
      <input type="hidden" name="xxTrustedFormCertUrl" value="" data-id="element-826" data-mp="2rrxfqaahpjy" />

      {/* White top nav bar with centered logo */}
      <header className="w-full bg-white border-b border-gray-100 py-5 px-4" data-id="element-827" data-mp="319x6qfptw11y">
        <div className="max-w-7xl mx-auto flex items-center justify-center relative" data-id="element-828" data-mp="368ib8rc456w7">
          {university.logoUrl ? <img src={university.logoUrl} alt={university.name} decoding="async" className="h-12 md:h-14 object-contain" data-id="element-829" data-mp="3b71u2iveu2kc" /> : <span className="text-xl font-bold" style={{
          color: university.primaryColor
        }} data-id="element-830" data-mp="qi5gwset6g2x">
              {university.name}
            </span>}
          <div className="hidden md:flex items-center gap-1.5 text-gray-400 text-xs font-medium absolute right-0" data-id="element-831" data-mp="2ri0lonmc9duo">
            <LockIcon className="w-3.5 h-3.5" data-id="element-832" data-mp="2mmlbc5nji030" />
            Secure
          </div>
        </div>
      </header>

      {/* Branded main wrapper — neutral surface for the supporting sections */}
      <main className="flex-grow bg-white" data-id="element-833" data-mp="2wjg7wj095qen">
        {/* HERO — full-bleed brand color, optionally overlaid with a partner image */}
        <section className="relative py-12 md:py-20 px-4 bg-cover bg-center" style={{
        backgroundColor: university.primaryColor,
        backgroundImage: university.heroBackgroundImage ? `url(${university.heroBackgroundImage})` : undefined
      }} data-id="element-834" data-mp="23tzm447517re">
          {/* Mobile-only eyebrow + H1 — rendered ABOVE the grid so the form
           can sit directly beneath it and stay above the fold on phones.
           Desktop renders the eyebrow + H1 inside the left column instead. */}
          <div className="relative max-w-6xl mx-auto lg:hidden text-white mb-6" data-id="element-835" data-mp="1ay05lan3wooq">
            <p className="text-sm font-semibold mb-3 text-white/90" data-id="element-836" data-mp="1frcnl1xa5x8i">
              {university.name}
            </p>
            <h1 className="text-4xl md:text-5xl font-extrabold leading-[1.05] tracking-tight" data-id="element-837" data-mp="112yfocl0bil3">
              {heroTitle}
            </h1>
          </div>

          <div className="relative max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start" data-id="element-838" data-mp="1tpj5v9pqxon2">
            {/* Left: Branded hero copy — appears BELOW the form on mobile,
             beside it on desktop. */}
            <div className="text-white lg:pt-8 order-2 lg:order-1" data-id="element-839" data-mp="1jr3ed5mt4dd1">
              {/* Desktop-only eyebrow + H1 (mobile renders these above the grid). */}
              <p className="hidden lg:block text-base font-semibold mb-4 text-white/90" data-id="element-840" data-mp="8czjzycb4n7g">
                {university.name}
              </p>
              <h1 className="hidden lg:block text-4xl md:text-5xl lg:text-6xl font-extrabold leading-[1.05] tracking-tight mb-6" data-id="element-841" data-mp="dclysc7sabid">
                {heroTitle}
              </h1>
              {heroDescription && <p className="text-base md:text-lg text-white/80 leading-relaxed max-w-lg" data-id="element-842" data-mp="oqnt9oskjalf">
                  {heroDescription}
                </p>}
              {university.landingContent?.heroHighlights && university.landingContent.heroHighlights.length > 0 && <ul className="mt-6 space-y-2.5 max-w-lg" data-id="element-843" data-mp="1ch1qrutxa1ne">
                    {university.landingContent.heroHighlights.map(h => <li key={h} className="flex items-start gap-2.5 text-sm md:text-base text-white/90" data-id="element-844" data-mp="12jdis37b2xna">
                        <CheckCircleIcon className="w-5 h-5 flex-shrink-0 mt-0.5 text-white/70" data-id="element-845" data-mp="xmwvfb7xknb9" />
                        <span data-id="element-846" data-mp="3n7rvsly4jlri">{h}</span>
                      </li>)}
                  </ul>}
            </div>

            {/* Right: Form card — appears directly under the H1 on mobile
             (above the fold), to the right on desktop. */}
            <div className="bg-white rounded-2xl shadow-xl border border-gray-200 p-6 md:p-8 lg:p-10 order-1 lg:order-2" data-id="element-847" data-mp="22sdc4m44ksgd">
              {/* Progress Indicator */}
              <div className="mb-6" data-id="element-848" data-mp="2chws9k0ge87j">
                <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4" data-id="element-849" data-mp="2ciy3ppldswps">
                  {university.formHeading || 'Take the next step in your education.'}
                </h2>
                <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden" data-id="element-850" data-mp="2lgqk6x2uaaer">
                  <div className="h-full transition-all duration-300" style={{
                  width: `${(step - firstStep + 1) / totalSteps * 100}%`,
                  backgroundColor: university.ctaColor
                }} data-id="element-851" data-mp="2qig0ar4x5e9p" />
                </div>
              </div>

              {step > firstStep && <button onClick={() => {
              setStep(step - 1);
              setError('');
            }} className="flex items-center gap-2 text-gray-500 hover:text-gray-900 mb-6 transition-colors" data-id="element-852" data-mp="t18y94d4uq9d">
                  <ArrowLeftIcon className="w-4 h-4" data-id="element-853" data-mp="y14zwwycy5lr" />
                  <span className="text-sm font-medium" data-id="element-854" data-mp="xzafrkuwuhiq">Back</span>
                </button>}

              {/* Step 0: Campus lookup by zip (campus-aware advertisers only) */}
              {step === 0 && hasCampusFlow && <div className="space-y-6" data-id="element-855" data-mp="1lqg28ivcw820">
                  <p className="text-sm text-gray-600 text-center -mt-2" data-id="element-856" data-mp="1lp4q9vrrbtzt">
                    Enter your ZIP code to see programs available at your{' '}
                    {university.shortName} campus.
                  </p>

                  <div data-id="element-857" data-mp="1vmj55qy18aq0">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-858" data-mp="1qpj1tnskhm2l">
                      ZIP Code
                    </label>
                    <input type="text" inputMode="numeric" value={formData.zipCode} onChange={e => {
                  const val = e.target.value.replace(/\D/g, '').slice(0, 5);
                  setCampusLookupError('');
                  // Auto-resolve campus when the zip becomes valid.
                  // If no match, leave the dropdown alone so the
                  // user can still pick manually.
                  if (val.length === 5 && university.campusZipLookup) {
                    const match = university.campusZipLookup(val);
                    if (match) {
                      setFormData(prev => ({
                        ...prev,
                        zipCode: val,
                        campusId: match.id,
                        campusName: match.name
                      }));
                      return;
                    }
                  }
                  updateField('zipCode', val);
                }} placeholder="e.g. 93291" maxLength={5} className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent text-lg" style={{
                  '--tw-ring-color': university.primaryColor
                } as any} data-id="element-859" data-mp="2acniyte4vswr" />
                  </div>

                  <div data-id="element-860" data-mp="vfum0w27w27s">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-861" data-mp="vgm7vf60ilkv">
                      Campus
                    </label>
                    <select value={formData.campusId || ''} disabled aria-readonly="true" tabIndex={-1} className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-lg text-gray-700 appearance-none cursor-not-allowed" style={selectStyle as any} data-id="element-862" data-mp="qi2krbfon3qx">
                      <option value="" data-id="element-863" data-mp="qah6j9trw35m">
                        -- Enter ZIP to match a campus --
                      </option>
                      {(university.landingContent?.campusList || []).map(c => <option key={c.id} value={c.id} data-id="element-864" data-mp="3trzav7ac6s1a">
                            {c.name} Campus
                          </option>)}
                    </select>
                    <p className="mt-2 text-xs text-gray-500" data-id="element-865" data-mp="7d63de50spkw">
                      Your campus is automatically determined by your ZIP code.
                    </p>
                  </div>

                  {campusLookupError && <div className="p-3 bg-red-50 border border-red-200 rounded-lg" data-id="element-866" data-mp="2jaaovvlg0bod">
                      <p className="text-red-600 text-sm" data-id="element-867" data-mp="2j87rgqhyng9m">
                        {campusLookupError}
                      </p>
                    </div>}
                </div>}

              {/* Step 1: Program Selection — single dropdown variant */}
              {step === 1 && university.singleProgramSelect && <div className="space-y-6" data-id="element-868" data-mp="20irmnqz8t1ne">
                  <div data-id="element-869" data-mp="20hpz4n11imos">
                    <label htmlFor="program-select" className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-870" data-mp="25a9cbivtfft2">
                      Program of Interest
                    </label>
                    <select id="program-select" value={formData.program} onChange={e => {
                  const selected = programsForThisFlow.find(p => p.name === e.target.value);
                  updateField('program', e.target.value);
                  updateField('degreeLevel', selected?.degreeLevel || '');
                  updateField('areaOfStudy', selected?.category || '');
                }} className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:border-transparent appearance-none cursor-pointer" style={{
                  ...selectStyle,
                  '--tw-ring-color': university.primaryColor
                } as any} data-id="element-871" data-mp="1vl7x3ux3h2g5">
                      <option value="" data-id="element-872" data-mp="1qocbx1uddihp">-- Select Program --</option>
                      {programsForThisFlow.map(p => <option key={p.name} value={p.name} data-id="element-873" data-mp="24l8z8tba4htu">
                          {p.name}
                        </option>)}
                    </select>
                  </div>
                </div>}

              {/* Step 1: Program Selection */}
              {step === 1 && !university.singleProgramSelect && <div className="space-y-6" data-id="element-874" data-mp="16apto5jlhdsy">
                  <div data-id="element-875" data-mp="169xygvtwti37">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-876" data-mp="1b30wbg5pzdp7">
                      Degree of Interest
                    </label>
                    <select value={formData.degreeLevel} onChange={e => {
                  updateField('degreeLevel', e.target.value);
                  updateField('areaOfStudy', '');
                  updateField('program', '');
                }} className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:border-transparent appearance-none cursor-pointer" style={{
                  ...selectStyle,
                  '--tw-ring-color': university.primaryColor
                } as any} data-id="element-877" data-mp="1g1au7yitwsei">
                      <option value="" data-id="element-878" data-mp="3ekfmkbz74nfx">-- Select Degree of Interest --</option>
                      {university.degreeLevels.map(level => <option key={level} value={level} data-id="element-879" data-mp="39i86ba7c2l7s">
                          {level}
                        </option>)}
                    </select>
                  </div>

                  <div data-id="element-880" data-mp="61rk2167dj3c">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-881" data-mp="147w29jpdry7">
                      Area of Study
                    </label>
                    <select value={formData.areaOfStudy} onChange={e => {
                  updateField('areaOfStudy', e.target.value);
                  updateField('program', '');
                }} disabled={!formData.degreeLevel} className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:border-transparent appearance-none cursor-pointer disabled:bg-gray-50 disabled:text-gray-400" style={{
                  ...selectStyle,
                  '--tw-ring-color': university.primaryColor
                } as any} data-id="element-882" data-mp="3t7qzoib10fi5">
                      <option value="" data-id="element-883" data-mp="x9886zqpf3qj">-- Select Area of Study --</option>
                      {uniqueAreas.map(area => <option key={area} value={area} data-id="element-884" data-mp="nbtrr04s6ey8">
                          {area}
                        </option>)}
                    </select>
                  </div>

                  <div data-id="element-885" data-mp="3822rn2qfl596">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-886" data-mp="382b4iumk0akd">
                      Program
                    </label>
                    <select value={formData.program} onChange={e => updateField('program', e.target.value)} disabled={!formData.areaOfStudy} className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:border-transparent appearance-none cursor-pointer disabled:bg-gray-50 disabled:text-gray-400" style={{
                  ...selectStyle,
                  '--tw-ring-color': university.primaryColor
                } as any} data-id="element-887" data-mp="30gp5e221sx7p">
                      <option value="" data-id="element-888" data-mp="2mpu358m21ed3">-- Select Program --</option>
                      {availablePrograms.map(p => <option key={p.name} value={p.name} data-id="element-889" data-mp="1u2hfev45r0i7">
                          {p.name}
                        </option>)}
                    </select>
                  </div>
                </div>}

              {/* Step 2: Academic Background */}
              {step === 2 && <div className="space-y-6" data-id="element-890" data-mp="2sif31wnuldxe">
                  <div data-id="element-891" data-mp="2sjp1wo2iyq53">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-892" data-mp="2sl9o9qeor2rv">
                      Highest Level of Education
                    </label>
                    <select value={formData.highestEducation} onChange={e => updateField('highestEducation', e.target.value)} className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:border-transparent appearance-none cursor-pointer" style={{
                  ...selectStyle,
                  '--tw-ring-color': university.primaryColor
                } as any} data-id="element-893" data-mp="20tbvo80nzngj">
                      <option value="" data-id="element-894" data-mp="19jpkr79y5rkb">-- Please Select --</option>
                      <option value="high-school" data-id="element-895" data-mp="19mlhisa26a76">
                        High School Diploma/GED
                      </option>
                      <option value="some-college" data-id="element-896" data-mp="zuz54slzi52u">Some College</option>
                      <option value="associates" data-id="element-897" data-mp="vsugk8ae7yn2">Associate's Degree</option>
                      <option value="bachelors" data-id="element-898" data-mp="vu5h7z8jkq9y">Bachelor's Degree</option>
                      <option value="some-high-school" data-id="element-899" data-mp="qsyck91p477i">Some High School</option>
                    </select>
                  </div>

                  <div data-id="element-900" data-mp="lwr68f5e0tp1">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-901" data-mp="gvtmhy4qbrlf">
                      When would you like to start classes?
                    </label>
                    <select value={formData.startDate} onChange={e => updateField('startDate', e.target.value)} className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:border-transparent appearance-none cursor-pointer" style={{
                  ...selectStyle,
                  '--tw-ring-color': university.primaryColor
                } as any} data-id="element-902" data-mp="2g6l97sibsehh">
                      <option value="" data-id="element-903" data-mp="11g3ti6fognui">-- Please Select --</option>
                      <option value="immediately" data-id="element-904" data-mp="16ef2key5eubo">Immediately</option>
                      <option value="1-3-months" data-id="element-905" data-mp="sduk5jw5b0ip">1-3 months</option>
                      <option value="3-6-months" data-id="element-906" data-mp="weo6w9xuq9a0">3-6 months</option>
                      <option value="6-12-months" data-id="element-907" data-mp="wexyu32giqic">6-12 months</option>
                      <option value="1-year-plus" data-id="element-908" data-mp="2ms58zscqpuoh">1+ year</option>
                      <option value="undecided" data-id="element-909" data-mp="2qsp831gvj8d9">Undecided</option>
                    </select>
                  </div>

                  <div data-id="element-910" data-mp="f4u2f38vro6s">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-911" data-mp="f6550zqlapwb">
                      High School Graduation Year
                    </label>
                    <select value={formData.hsGradYear} onChange={e => updateField('hsGradYear', e.target.value)} className="w-full px-4 py-3 bg-white border-2 border-gray-200 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:border-transparent appearance-none cursor-pointer" style={{
                  ...selectStyle,
                  '--tw-ring-color': university.primaryColor
                } as any} data-id="element-912" data-mp="ouvhx6n63ya3">
                      <option value="" data-id="element-913" data-mp="39lmhkfuovkxp">-- Please Select --</option>
                      {years.map(year => <option key={year} value={year} data-id="element-914" data-mp="34odt5xiy6ko0">
                          {year}
                        </option>)}
                      <option value="1989" data-id="element-915" data-mp="1fuijcdd8bvst">1989 or earlier</option>
                    </select>
                  </div>
                </div>}

              {/* Step 3: Location */}
              {step === 3 && <div className="space-y-6" data-id="element-916" data-mp="1osvjk13t7db0">
                  {formData.city && formData.state ? <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3" data-id="element-917" data-mp="23q4pq0xrulms">
                      <span className="text-green-600 text-xl" data-id="element-918" data-mp="1yqicvsluv7ei">📍</span>
                      <div className="flex-1" data-id="element-919" data-mp="2cipvmggb0xu7">
                        <p className="text-sm font-semibold text-gray-900" data-id="element-920" data-mp="28ie9ez8ampgm">
                          {formData.city}, {formData.state} {formData.zipCode}
                        </p>
                      </div>
                      <button type="button" onClick={() => {
                  updateField('city', '');
                  updateField('state', '');
                  updateField('zipCode', '');
                }} className="text-sm text-green-600 hover:text-green-700 font-semibold" data-id="element-921" data-mp="2hjcihvxl9n62">
                        change
                      </button>
                    </div> : <div data-id="element-922" data-mp="2ie4obpaixidy">
                      <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-923" data-mp="2icto1zgaesrl">
                        ZIP Code
                      </label>
                      <input type="text" inputMode="numeric" value={formData.zipCode} onChange={e => {
                  const val = e.target.value.replace(/\D/g, '').slice(0, 5);
                  updateField('zipCode', val);
                }} placeholder="e.g. 90210" maxLength={5} className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent" style={{
                  '--tw-ring-color': university.primaryColor
                } as any} data-id="element-924" data-mp="28ggvj4whakyf" />
                    </div>}

                  {formData.city && formData.state && <div className="animate-in fade-in slide-in-from-top-2 duration-300" data-id="element-925" data-mp="nwd5bvwgk8o7">
                      <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-926" data-mp="j1654y2edm41">
                        Street Address
                      </label>
                      <input type="text" value={formData.streetAddress} onChange={e => updateField('streetAddress', e.target.value)} placeholder="123 Main St" className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent" style={{
                  '--tw-ring-color': university.primaryColor
                } as any} data-id="element-927" data-mp="33qdcfiazn79i" />
                    </div>}
                </div>}

              {/* Step 4: Contact & Submit */}
              {step === 4 && <div className="space-y-6" data-id="element-928" data-mp="26xnj8sk2deql">
                  <p className="text-sm text-gray-600 text-center -mt-2" data-id="element-929" data-mp="2bwgtmc420pmj">
                    Where should {university.shortName} send your information?
                  </p>

                  <div className="grid grid-cols-2 gap-4" data-id="element-930" data-mp="hhxrwbrc6q5k">
                    <div data-id="element-931" data-mp="hifwpws2ecp3">
                      <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-932" data-mp="hjr0vxpf9mgq">
                        First Name
                      </label>
                      <input type="text" value={formData.firstName} onChange={e => updateField('firstName', e.target.value)} className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent" style={{
                    '--tw-ring-color': university.primaryColor
                  } as any} data-id="element-933" data-mp="3dd0rzc7de427" />
                    </div>
                    <div data-id="element-934" data-mp="34jnqhlv8pu4i">
                      <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-935" data-mp="2gvwcmdu1x914">
                        Last Name
                      </label>
                      <input type="text" value={formData.lastName} onChange={e => updateField('lastName', e.target.value)} className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent" style={{
                    '--tw-ring-color': university.primaryColor
                  } as any} data-id="element-936" data-mp="2bwjpdrz9pzy0" />
                    </div>
                  </div>

                  <div data-id="element-937" data-mp="115dcmapdumfj">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-938" data-mp="11426w5cdn4ls">
                      Email Address
                    </label>
                    <input type="email" value={formData.email} onChange={e => updateField('email', e.target.value)} className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent" style={{
                  '--tw-ring-color': university.primaryColor
                } as any} data-id="element-939" data-mp="3lv2r2mm6c67p" />
                  </div>

                  <div data-id="element-940" data-mp="1hoi1gkyh92af">
                    <label className="block text-sm font-semibold text-gray-700 mb-2" data-id="element-941" data-mp="1hmyi2lhiyb4f">
                      Phone Number
                    </label>
                    <input type="tel" value={formData.phone} onChange={e => updateField('phone', formatPhoneNumber(e.target.value))} placeholder="555-123-4567" className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:border-transparent" style={{
                  '--tw-ring-color': university.primaryColor
                } as any} data-id="element-942" data-mp="18nbhu30j7bcy" />
                  </div>

                  <div className="pt-4 space-y-3" data-id="element-943" data-mp="3adsges8hrj3s">
                    {university.tcpaTextSms && <label className="flex items-start gap-3 cursor-pointer p-4 rounded-lg border border-gray-200 bg-gray-50 hover:bg-gray-100 transition-colors" data-id="element-944" data-mp="3fcc34uuwsyx7">
                        <input type="checkbox" checked={formData.consentSms} onChange={e => updateField('consentSms', e.target.checked)} className="mt-1 w-5 h-5 border-gray-300 rounded focus:ring-2 focus:ring-offset-2" style={{
                    accentColor: university.primaryColor
                  }} data-id="element-945" data-mp="2iofldmnqjxke" />
                        <span className="text-xs text-gray-600 leading-relaxed" data-id="element-946" data-mp="wznwado86wx1">
                          {university.tcpaTextSms}
                        </span>
                      </label>}
                    {university.consentOnSubmit ?
                // Consent is given by clicking the submit button, so
                // the disclosure is shown as plain text.
                <p className="text-xs text-gray-600 leading-relaxed p-4 rounded-lg border border-gray-200 bg-gray-50" data-id="element-947" data-mp="1gneujxqkx0rs">
                        {university.tcpaText}
                      </p> : <label className="flex items-start gap-3 cursor-pointer p-4 rounded-lg border border-gray-200 bg-gray-50 hover:bg-gray-100 transition-colors" data-id="element-948" data-mp="1pnbfppdrfhi3">
                        <input type="checkbox" checked={formData.consent} onChange={e => updateField('consent', e.target.checked)} className="mt-1 w-5 h-5 border-gray-300 rounded focus:ring-2 focus:ring-offset-2" style={{
                    accentColor: university.primaryColor
                  }} data-id="element-949" data-mp="3rfpxmogb0lyi" />
                        <span className="text-xs text-gray-600 leading-relaxed" data-id="element-950" data-mp="1x72kkd13tqe0">
                          {university.tcpaText || `By checking this box, I consent to receive calls and text messages from ${university.name} at the number provided above regarding educational opportunities. I understand that these calls may be generated using an automated technology and that my consent is not required to enroll. Message and data rates may apply.`}
                        </span>
                      </label>}
                  </div>
                </div>}

              {error && <div className="mt-6 p-3 bg-red-50 border border-red-200 rounded-lg" data-id="element-951" data-mp="38ky8aa62r7gu">
                  <p className="text-red-600 text-sm" data-id="element-952" data-mp="f4xvaj5vh4ho">{error}</p>
                </div>}

              <div className="mt-8" data-id="element-953" data-mp="k412a95exksa">
                {step < 4 ? <button onClick={handleContinue} disabled={addressChecking} className="w-full py-4 font-bold rounded-lg text-lg transition-colors shadow-md hover:shadow-lg disabled:cursor-wait disabled:opacity-70" style={{
                backgroundColor: university.ctaColor,
                color: university.ctaTextColor || '#ffffff'
              }} data-id="element-954" data-mp="o6xf1j89umob">
                    {addressChecking ? 'Checking your address…' : 'Continue'}
                  </button> : <button onClick={handleSubmit} disabled={isSubmitting || !university.consentOnSubmit && !formData.consent || Boolean(university.tcpaTextSms) && !formData.consentSms} className="w-full py-4 font-bold rounded-lg text-lg transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed" style={{
                backgroundColor: university.ctaColor,
                color: university.ctaTextColor || '#ffffff'
              }} data-id="element-955" data-mp="39onkpdkmmnqh">
                    {isSubmitting ? 'Submitting...' : university.submitLabel || 'Submit Application'}
                  </button>}
              </div>
            </div>
          </div>
        </section>
        {/* /HERO */}

        {/* Supporting site content — renders on every step so the
            program page (deep link) reads as a full marketing site,
            not just a form. */}
        {university.landingContent && <>
            {/* Stats strip */}
            {university.landingContent.stats && <section className="bg-white border-b border-gray-100 py-10 md:py-14 px-4" data-id="element-956" data-mp="265la8fb8t0ku">
                <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8" data-id="element-957" data-mp="1s63fljb9mt3k">
                  {university.landingContent.stats.map(s => <div key={s.label} className="text-center" data-id="element-958" data-mp="1w98elbw50cng">
                      <div className="text-3xl md:text-4xl font-extrabold mb-1" style={{
                color: university.primaryColor
              }} data-id="element-959" data-mp="1iez2yq92bkbw">
                        {s.value}
                      </div>
                      <div className="text-xs md:text-sm font-semibold uppercase tracking-wider text-gray-500" data-id="element-960" data-mp="1dh79hrsrrq5l">
                        {s.label}
                      </div>
                    </div>)}
                </div>
              </section>}

            {/* Why us — feature grid */}
            <section className="bg-gray-50 py-16 md:py-24 px-4" data-id="element-961" data-mp="kwaapbe0dzkv">
              <div className="max-w-6xl mx-auto" data-id="element-962" data-mp="fwegg3v45mii">
                <div className="text-center max-w-2xl mx-auto mb-12" data-id="element-963" data-mp="tuvz3n65w8tk">
                  <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{
                color: university.ctaColor
              }} data-id="element-964" data-mp="tw75luqzruod">
                    Why {university.shortName}
                  </p>
                  <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 leading-tight" data-id="element-965" data-mp="ytfl1nkr9klx">
                    Education built around your career goals.
                  </h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-966" data-mp="18kk0eoecf7ha">
                  {university.landingContent.features.map(f => {
                const Icon = featureIconMap[f.icon] || GraduationCapIcon;
                return <div key={f.title} className="bg-white rounded-xl border border-gray-200 p-6 text-center hover:shadow-md transition-shadow" data-id="element-967" data-mp="3bzm0ijrwr0jv">
                        <div className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4" style={{
                    backgroundColor: `${university.ctaColor}15`
                  }} data-id="element-968" data-mp="3lr06naed6ylz">
                          <Icon className="w-7 h-7" style={{
                      color: university.ctaColor
                    }} data-id="element-969" data-mp="2y1flckuqnp9j" />
                        </div>
                        <h3 className="text-sm font-bold tracking-widest uppercase text-gray-900 mb-2" data-id="element-970" data-mp="hcryc3lmhd8s">
                          {f.title}
                        </h3>
                        <p className="text-sm text-gray-600 leading-relaxed" data-id="element-971" data-mp="md68br6sax9g">
                          {f.description}
                        </p>
                      </div>;
              })}
                </div>
              </div>
            </section>

            {/* Info sections — alternating layout */}
            {university.landingContent.infoSections && university.landingContent.infoSections.map((s, i) => <section key={s.heading} className={`py-16 md:py-24 px-4 ${i % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`} data-id="element-972" data-mp="dpleso9v55pl" data-mpi={i}>
                  <div className="max-w-5xl mx-auto" data-id="element-973" data-mp="3pujwlhe4ooz">
                    {s.eyebrow && <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{
              color: university.ctaColor
            }} data-id="element-974" data-mp="3vux0obz2p76h">
                        {s.eyebrow}
                      </p>}
                    <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 leading-tight mb-5 max-w-3xl" data-id="element-975" data-mp="3nkzgkupgkysi">
                      {s.heading}
                    </h2>
                    <p className="text-base md:text-lg text-gray-600 leading-relaxed mb-6 max-w-3xl" data-id="element-976" data-mp="3elvv5k2tk7au">
                      {s.body}
                    </p>
                    {s.bullets && <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3 max-w-3xl" data-id="element-977" data-mp="a6csf42iuqj7">
                        {s.bullets.map(b => <li key={b} className="flex items-start gap-3 text-gray-700" data-id="element-978" data-mp="f5672ztjonky">
                            <CheckCircleIcon className="w-5 h-5 flex-shrink-0 mt-0.5" style={{
                  color: university.ctaColor
                }} data-id="element-979" data-mp="63ycosu5g6w5" />
                            <span className="text-sm md:text-base" data-id="element-980" data-mp="2idq4x1s0jxv8">{b}</span>
                          </li>)}
                      </ul>}
                  </div>
                </section>)}

            {/* Community image — partner-supplied lifestyle photo */}
            {university.landingContent.communityImage && <section className="bg-white py-16 md:py-24 px-4" data-id="element-981" data-mp="1zo01jqa6q3rp">
                <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 items-center" data-id="element-982" data-mp="1zo9rxez56cxx">
                  <div className="order-2 md:order-1" data-id="element-983" data-mp="1umu8frcm4ehn">
                    {university.landingContent.communityImage.eyebrow && <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{
                color: university.ctaColor
              }} data-id="element-984" data-mp="24ml1rppfsng5">
                        {university.landingContent.communityImage.eyebrow}
                      </p>}
                    {university.landingContent.communityImage.heading && <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 leading-tight mb-5" data-id="element-985" data-mp="vefql4q2iwk7">
                        {university.landingContent.communityImage.heading}
                      </h2>}
                    {university.landingContent.communityImage.body && <p className="text-base md:text-lg text-gray-600 leading-relaxed" data-id="element-986" data-mp="19dovv9cqk69e">
                        {university.landingContent.communityImage.body}
                      </p>}
                  </div>
                  <div className="order-1 md:order-2 flex justify-center" data-id="element-987" data-mp="1o2uzramiqknm">
                    <img src={university.landingContent.communityImage.url} alt={university.landingContent.communityImage.alt || university.name} loading="lazy" decoding="async" className="w-full max-w-md object-contain" data-id="element-988" data-mp="1o4xolrbxq7qr" />
                  </div>
                </div>
              </section>}

            {/* Testimonial */}
            {university.landingContent.testimonial && <section className="py-16 md:py-24 px-4" style={{
          backgroundColor: `${university.primaryColor}08`
        }} data-id="element-989" data-mp="33nx2p86fegqp">
                <div className="max-w-3xl mx-auto text-center" data-id="element-990" data-mp="76quco5x9h3">
                  <div className="text-6xl leading-none mb-4 font-serif" style={{
              color: university.primaryColor
            }} data-id="element-991" data-mp="3riq1xto3kf20">
                    "
                  </div>
                  <blockquote className="text-xl md:text-2xl font-medium text-gray-900 leading-relaxed mb-6" data-id="element-992" data-mp="j47w7ylmpblk">
                    {university.landingContent.testimonial.quote}
                  </blockquote>
                  <div className="text-sm text-gray-600" data-id="element-993" data-mp="vrkassai1am5">
                    <span className="font-bold text-gray-900" data-id="element-994" data-mp="vu6g0l9x3e17">
                      {university.landingContent.testimonial.name}
                    </span>
                    {' — '}
                    {university.landingContent.testimonial.program}
                  </div>
                </div>
              </section>}

            {/* Career section with image */}
            {university.landingContent.careerSection && <section className="bg-white py-16 md:py-24 px-4" data-id="element-995" data-mp="19rcuw4rissl5">
                <div className="max-w-6xl mx-auto rounded-2xl border border-gray-200 overflow-hidden shadow-sm" data-id="element-996" data-mp="19piacrk5v2hl">
                  <div className="grid grid-cols-1 md:grid-cols-2 items-stretch" data-id="element-997" data-mp="2285r5us4jvcv">
                    <div className="p-8 md:p-12 flex flex-col justify-center" data-id="element-998" data-mp="22a0cvbb7zri0">
                      <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900 leading-tight mb-5" data-id="element-999" data-mp="1xg5vwfn01qmm">
                        {university.landingContent.careerSection.headline}
                      </h2>
                      <p className="text-base md:text-lg text-gray-600 leading-relaxed mb-6" data-id="element-1000" data-mp="3l5eb8254yk2x">
                        {university.landingContent.careerSection.body}
                      </p>
                      <button onClick={() => {
                  window.scrollTo({
                    top: 0,
                    behavior: 'smooth'
                  });
                }} className="self-start px-7 py-3 text-white font-bold rounded-lg transition-all shadow-md hover:shadow-lg" style={{
                  backgroundColor: university.ctaColor
                }} data-id="element-1001" data-mp="3u59smopabbbe">
                        Get Started
                      </button>
                    </div>
                    <div className="min-h-[280px] md:min-h-0" data-id="element-1002" data-mp="2mqe5258f8lyb">
                      <img src={university.landingContent.careerSection.imageUrl} alt={university.landingContent.careerSection.imageAlt || university.name} loading="lazy" decoding="async" className="w-full h-full object-cover" data-id="element-1003" data-mp="2mqxm7jq8z6ba" />
                    </div>
                  </div>
                </div>
              </section>}

            {/* Final CTA band */}
            {university.landingContent.finalCta && <section className="py-16 md:py-20 px-4" style={{
          backgroundColor: university.primaryColor
        }} data-id="element-1004" data-mp="3ag8gcid3e2gg">
                <div className="max-w-4xl mx-auto text-center text-white" data-id="element-1005" data-mp="1g1xt2ec5bgcs">
                  <h2 className="text-3xl md:text-4xl font-extrabold leading-tight mb-4" data-id="element-1006" data-mp="1g425xls0vpp7">
                    {university.landingContent.finalCta.headline}
                  </h2>
                  <p className="text-base md:text-lg text-white/85 leading-relaxed mb-8 max-w-2xl mx-auto" data-id="element-1007" data-mp="ignsf9unjbs">
                    {university.landingContent.finalCta.body}
                  </p>
                  <button onClick={() => {
              window.scrollTo({
                top: 0,
                behavior: 'smooth'
              });
            }} className="px-8 py-4 font-bold rounded-lg text-base md:text-lg transition-all shadow-lg hover:shadow-xl" style={{
              backgroundColor: university.ctaColor,
              color: '#fff'
            }} data-id="element-1008" data-mp="3mx9s5zjvd5ny">
                    {university.landingContent.finalCta.buttonLabel || 'Request Information'}
                  </button>
                </div>
              </section>}
          </>}

        {/* Branded footer */}
        <footer className="bg-gray-900 text-gray-400 py-8 px-4 text-center text-xs" data-id="element-1009" data-mp="2u9dagjcgiqqr">
          <div className="max-w-5xl mx-auto space-y-2" data-id="element-1010" data-mp="2pajywwgqd9t8">
            <p data-id="element-1011" data-mp="2p8z4r7ybvsvw">
              © {new Date().getFullYear()} {university.name}. All rights
              reserved.
            </p>
            <p className="text-gray-500" data-id="element-1012" data-mp="2fcmcmeifln39">
              {university.accreditation} • Founded {university.founded}
            </p>
            <div className="max-w-2xl mx-auto pt-2" data-id="element-1013" data-mp="2alnquzeobwsm">
              <TrackingNoticeLinks data-id="element-1014" data-mp="2ak2xve80clwv" />
            </div>
          </div>
        </footer>
      </main>
    </div>;
}